import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ActivatedRouteSnapshot, Data, NavigationEnd, Router } from '@angular/router';
import { Breadcrumb } from '../interfaces/utils/breadcrumb';

@Injectable({
	providedIn: 'root'
})
export class BreadcrumbService {

	// Subject emitting the breadcrumb hierarchy 
	private readonly _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);
	private readonly _breadcrumbsActive$ = new BehaviorSubject<Breadcrumb| null>(null);

	// Observable exposing the breadcrumb hierarchy 
	readonly breadcrumbs$ = this._breadcrumbs$.asObservable();

	// Exposing active
	readonly breadcrumbsActive$ = this._breadcrumbsActive$.asObservable();

	constructor(private router: Router) {
		this.router.events.pipe(
			// Filter the NavigationEnd events as the breadcrumb is updated only when the route reaches its end 
			filter(event => event instanceof NavigationEnd)
		).subscribe(() => {
			// Construct the breadcrumb hierarchy 
			const root = this.router.routerState.snapshot.root;
			const breadcrumbs: Breadcrumb[] = [];
			this.addBreadcrumb(root, [], breadcrumbs);

			// Emit the new hierarchy 
			this._breadcrumbs$.next(breadcrumbs);
			// Emit actual route
			this._breadcrumbsActive$.next(breadcrumbs[breadcrumbs.length - 1]);
		});
	}

	private addBreadcrumb(route: ActivatedRouteSnapshot | null, parentUrl: string[], breadcrumbs: Breadcrumb[]) {
		if (route) {
			// Construct the route URL 
			const routeUrl = parentUrl.concat(route.url.map(url => url.path));

			// Add an element for the current route part 
			if (route.data['breadcrumb']) {
				const finalRouteUrl = `/${routeUrl.join('/')}`;
				const findElement = breadcrumbs.some(b => b.url === finalRouteUrl);
				if (!findElement) {
					const hasDisabled = breadcrumbs.some(b => b.disableLink);
					let disableLink = route.data['disableLink'] ?? false;
					if (hasDisabled) {
						disableLink = false;
					}
					const breadcrumb: Breadcrumb = {
						label: this.getLabel(route.data),
						disableLink,
						url: finalRouteUrl,
						actions: route.data['actions'] ?? [],
						buttons: route.data['buttons'] ?? [],
						params: route.params,
						isModule: route.data['isModule'] ?? false
					};
					breadcrumbs.push(breadcrumb);
				}
			}

			// Add another element for the next route part 
			this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
		}
	}

	private getLabel(data: Data) {
		// The breadcrumb can be defined as a static string or as a function to construct the breadcrumb element out of the route data 
		const breadcrumbProp = data['breadcrumb'];
		return typeof breadcrumbProp === 'function' ? breadcrumbProp(data) : breadcrumbProp;
	}
}
